import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import classnames from 'classnames';
import { debounce } from 'lodash-es';
import { Icon, Layout, Justify, Segment, SearchBox, Bubble } from 'tea-component';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import Tabs from '@intelligence/components/NewIconTabs';
import Loading from '@/pages/intelligence/components/Loading';
import { ReportPageType } from '@intelligence/pages/InsightsReport/_config';
import { segmentListMap } from '@intelligence/pages/InsightsCenterLibrary/_config';
import InsightsReportCard from '@intelligence/components/ReportCard/InsightsReportCard';
import { IInsightsCenterLibraryTab } from '@intelligence/pages/InsightsCenterLibrary';
import iccReportIcon from '@intelligence/assets/svg/iccReport/insightsCenterLibrary.svg';
import useData from '@intelligence/pages/InsightsCenterLibrary/_hooks';
import { IS_WOA } from '@home/_config/config';
import SortBySelector, {
  OrderTypeMap,
} from '@intelligence/pages/InsightsReport/components/SortBySelector';
import { sortReportsData } from '@intelligence/pages/InsightsReport/_utils';

import './index.scss';
import '@/pages/intelligence/components/LazyReport/index.scss';
import SearchBar from '../InsightsReport/SearchBar';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

const { Content } = Layout;

const NEW_MARK_IDS_MAPPING_KEY = 'ReportsInsightsReports';

export interface ReportProps {
  title: string;
  subTitle: string;
  tabs?: any[];
  reportType: string;
  getReportFun: Function;
  searchType: string;
  pageLogMes: any;
  reportTypeConfig: ReportPageType;
  preClassName?: string;
}

export default function InsightsCenterLibraryReport(props: ReportProps) {
  const { formatMessage } = useIntl();

  const {
    title,
    subTitle,
    tabs = [],
    reportType,
    getReportFun,
    reportTypeConfig,
    preClassName = 'dbi-insights-center-library',
  } = props;

  const {
    activeId,
    setActiveId,
    segmentList,
    activeSegment,
    setActiveSegment,
    onSearch,
    handleSearchRecentClick,
    deleteSearchRecent,
    cacheRecentSearch,
    dataLoading,
    dataPartial,
    currentTabSegmentData,
    lang,
    search,
    onClear,
    shareBack,
    lazyScrollRef,
    data,
    loading,
    loadingMore,
    noMore,
    handleTabContent,
    mutate,
    orderType,
    setOrderType,
    languageSegmentList,
    languageSegmentValue,
    setLanguageSegmentValue,
  } = useData(props);
  const scrollBody = useRef<HTMLElement>(null);
  const [segmentFixed, setSegmentFixed] = useState(false);
  const [scrollPageCount, setScrollPageCount] = useState(0);

  // 监听页面滚动 -- 吸顶
  useEffect(() => {
    if (scrollBody.current) {
      const handleScroll = debounce((e: any) => {
        const { scrollTop } = e.srcElement;
        setScrollPageCount((scrollPageCount) => scrollPageCount + 1);
        // 滚动判断距离 和定位条位置相关，开发调整
        if (reportType === 'insightsCenterLibrary') {
          if (scrollTop > 140) {
            setSegmentFixed(true);
          } else {
            setSegmentFixed(false);
          }
        } else {
          setSegmentFixed(false);
        }
      }, 100);
      scrollBody.current?.addEventListener('scroll', handleScroll, false);
      return () => scrollBody.current?.removeEventListener('scroll', handleScroll, false);
    }
  }, [scrollBody]);

  const handleTabChange = (tab: IInsightsCenterLibraryTab) => {
    const firstSegment = segmentListMap[tab.id]?.[0]?.value;
    setActiveId(tab.id);
    handleTabContent(tab.id, firstSegment);
    if (tab?.pagePoint) {
      buriedPointPage(tab?.pagePoint);
    }
  };

  const handleSegmentChange = (segment: string) => {
    setActiveSegment(segment);
    handleTabContent(activeId, segment);
  };

  const getTabList = () => (
    <div className={`header-tab ${preClassName}-tab`}>
      <Tabs
        ceiling
        newMarkIdsMappingKey={NEW_MARK_IDS_MAPPING_KEY}
        tabs={tabs}
        animated={false}
        activeId={activeId}
        onActive={(tab) => {
          handleTabChange(tab as IInsightsCenterLibraryTab);
        }}
      />
    </div>
  );

  interface IHandleCountsDataChangeProps {
    resourceId: string;
    field: 'report_like' | 'watched_cnt';
    counts?: number;
  }

  // 点赞数/浏览数变化更新数据并排序
  const handleCountsDataChange = ({ resourceId, field, counts }: IHandleCountsDataChangeProps) => {
    // 当前展示数据对应数据处理
    const newData: any = {
      ...data,
      list: data?.list?.map((item) => {
        if (resourceId === item?.resource_id) {
          const newCounts = Number(counts) > 0 ? counts : item?.[field] + 1;
          return { ...item, [field]: newCounts };
        }
        return item;
      }),
    };

    // 当前展示数据排序
    // [OrderTypeMap.likes, OrderTypeMap.views].includes(orderType) &&
    //   sortReportsData(newData.list, orderType);

    // 更新原始数据点赞/浏览数
    const currentReport: any = dataPartial?.find((item: any) => resourceId === item?.resource_id);
    const newCounts = Number(counts) > 0 ? counts : (currentReport?.[field] || 0) + 1;
    currentReport && (currentReport[field] = newCounts);

    mutate({ ...newData });
  };

  // 点赞量数据处理
  const handleLikeBtnClick = (resourceId: string, likeCounts?: number) => {
    handleCountsDataChange({ resourceId, counts: likeCounts, field: 'report_like' });
  };

  // 浏览量数据处理
  const handleViewsChange = (resourceId: string, viewCounts?: number) => {
    handleCountsDataChange({ resourceId, counts: viewCounts, field: 'watched_cnt' });
  };

  const handleSortByChange = (orderType: string) => {
    setOrderType(orderType);
  };
  const onClickSearchRecent = (item: any) => {
    handleSearchRecentClick(item);
  };

  return (
    <Content className={preClassName} ref={scrollBody}>
      <Content.Header
        title={title}
        className={classnames([
          'dbi-header contentHead-normal dbi-header-des',
          tabs?.length ? `${preClassName}-header` : 'dbi-report-header',
        ])}
      >
        <div className="header-des">
          <Bubble placement="bottom" content={subTitle} style={{ minWidth: 550 }}>
            <div className="header-des-text">{subTitle}</div>
          </Bubble>
        </div>
        <SearchBar
                  key={'reportSearchBar'}
                  preClassName={preClassName}
                  searchValue={search}
                  onSearch={onSearch}
                  onClear={onClear}
                  cacheRecentSearch={cacheRecentSearch}
                  onClickSearchRecent={onClickSearchRecent}
                  deleteSearchRecent={deleteSearchRecent}
                  scrollPageCount={scrollPageCount}
                />

        {Boolean(tabs?.length) && getTabList()}

        <img className="dbi-icc-report-icon" src={iccReportIcon} alt="" />
      </Content.Header>

      <Content.Body full ref={lazyScrollRef} className={`${preClassName}-body-full`}>
        <MarqueeBanner />
        {/* 吸顶内容 */}
        <div
          className={`${preClassName}-header-fixed header-segment ${
            segmentFixed ? 'visible' : 'hide'
          }`}
        >
          {segmentFixed && <>
            {/* <SearchBar
              key={'reportSearchBarFixed'}
              preClassName={preClassName}
              searchValue={search}
              onSearch={onSearch}
              onClear={onClear}
              cacheRecentSearch={cacheRecentSearch}
              onClickSearchRecent={onClickSearchRecent}
              deleteSearchRecent={deleteSearchRecent}
              scrollPageCount={scrollPageCount}
            /> */}
            {getTabList()}
          </>}
        </div>
        <Justify
          className="dbi-icc-report-operate"
          left={
            <>
              {Boolean(segmentList?.length) && (
                <Segment
                  className="segment-light"
                  options={segmentList}
                  value={activeSegment}
                  onChange={handleSegmentChange}
                />
              )}

              {languageSegmentList?.length > 1 && (
                <Segment
                  className="segment-light"
                  options={languageSegmentList}
                  value={languageSegmentValue}
                  onChange={(value: string) => setLanguageSegmentValue(value)}
                />
              )}

              {/* 排序选项 */}
              <SortBySelector defaultValue={orderType} onChange={handleSortByChange} />
            </>
          }
        />
        {loading ? (
          <div
            style={
              reportType === 'studioReport'
                ? { marginTop: 188, color: '#262832' }
                : { height: 'calc(100vh - 300px)', color: '#262832' }
            }
          >
            <Loading content={formatMessage({ id: 'common_loading' })} />
          </div>
        ) : (
          <div>
            {data?.list?.map((item) => (
              <InsightsReportCard
                key={item.resource_id}
                data={item}
                lang={lang}
                reportLangSegment={languageSegmentValue}
                isNew={
                  // !!moment(item?.update_time || item?.time).isAfter(moment().subtract(1, 'month'))
                  !!moment(item?.create_time || item?.time).isAfter(moment().subtract(1, 'month'))
                }
                shareBack={shareBack}
                getReportFun={getReportFun}
                reportTypeConfig={reportTypeConfig}
                needPopover={true}
                // pdfPreviewDownloadPage={'/pdfPreviewDownload'}
                pdfPreviewDownloadPage={'/pdfPreviewDownloadLikeShare'}
                onChange={handleLikeBtnClick}
                onViewsChange={handleViewsChange}
                // 除 woa 环境外，展示邮箱信息
                showContactInfo={!IS_WOA}
              />
            ))}
          </div>
        )}
        {loadingMore && (
          <div className="dbi-lazyreport-loading dbi-lazyreport-end">
            <Icon size="l" type="loading" />
            &nbsp;&nbsp;&nbsp;{formatMessage({ id: 'dbi_reports_loading_more' })}
          </div>
        )}

        {currentTabSegmentData?.length
          ? dataPartial.length <= 0 &&
            noMore &&
            !loading &&
            !dataLoading && (
              <div className={`dbi-reportcard-nodata ${preClassName}-nodata`}>
                {formatMessage({ id: 'dbi_reports_search_na_data' })}
              </div>
            )
          : dataPartial.length <= 0 &&
            noMore &&
            !loading &&
            !dataLoading && (
              <div className={`dbi-reportcard-nodata ${preClassName}-nodata`}>
                {formatMessage({ id: 'dbi_iic_reports_search_na_data' })}
              </div>
            )}
      </Content.Body>
    </Content>
  );
}
